import { IJob, JobPartCategory } from "../models/Job";
import { IPart } from "../models/Part";

/**
 * The one definition of "this part needs no more work on the line".
 *
 * Four things take a part off the dismantler's list: it was removed, it was found damaged, it
 * was found missing, or it couldn't be got at (not accessible). Missing was the one that kept
 * getting left out - a part flagged missing can never gain a RemovedOn, so anything counting only
 * removed/damaged held the job (and the vehicle behind it) open for good; not accessible has the
 * same shape. The API treats all four the same way (see VehicleDismantledRules.UnfinishedJobs and
 * ProductionManagerService's IsDone), so this keeps the dashboard's own progress, badges and
 * counts agreeing with it.
 */
export const isPartActioned = (part: IPart | null | undefined): boolean =>
  !!part && (!!part.removedOn || !!part.isDamaged || !!part.isMissing || !!part.isNotAccessible);

/**
 * The same test at job-part level. A missing partData means no part was ever created for the
 * category or the part was binned, and there is nothing left to wait on either way, so it counts
 * as done - matching the API, which closes a job on "PartData == null || ... ".
 */
export const isJobPartActioned = (jobPartCategory: JobPartCategory): boolean =>
  !jobPartCategory.partData || isPartActioned(jobPartCategory.partData);

/** Whether any work has been logged against the job yet, for the Not Started / In Progress split. */
export const hasJobStarted = (job: IJob | undefined): boolean =>
  !!job?.jobPartCategories?.some((jpc) => isPartActioned(jpc.partData));

/**
 * Whether every part on the job has been actioned. A job with no parts at all is never finished
 * by this test - there is nothing to judge, so only an explicit completedOn closes it.
 */
export const isJobDismantlingComplete = (job: IJob): boolean =>
  !!job.completedOn ||
  ((job.jobPartCategories?.length ?? 0) > 0 &&
    job.jobPartCategories.every(isJobPartActioned));
